import { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, X, Send, Trash2, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { API } from "@/lib/api";
import api from "@/lib/api";

const ChatContext = createContext(null);
export const useChat = () => useContext(ChatContext);

export function ChatProvider({ children }) {
  const [open, setOpen] = useState(false);
  const openChat = useCallback(() => setOpen(true), []);
  const closeChat = useCallback(() => setOpen(false), []);
  return (
    <ChatContext.Provider value={{ open, openChat, closeChat }}>
      {children}
    </ChatContext.Provider>
  );
}

function TypingDots() {
  return (
    <span className="inline-flex items-center gap-1" data-testid="cm-typing">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="w-1.5 h-1.5 rounded-full bg-gold/70 animate-bounce"
          style={{ animationDelay: `${i * 0.15}s` }}
        />
      ))}
    </span>
  );
}

export default function CommunityManagerChat() {
  const { user } = useAuth();
  const chat = useChat();
  const open = chat?.open;
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const scrollRef = useRef(null);
  const isMember = user && (user.role === "admin" || user.subscription_status === "active");

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    });
  }, []);

  useEffect(() => {
    if (open && isMember && !loaded) {
      api.get("/chat")
        .then(({ data }) => {
          setMessages((data.messages || []).map((m) => ({ id: m.msg_id, role: m.role, text: m.text })));
          setLoaded(true);
          scrollToBottom();
        })
        .catch(() => setLoaded(true));
    }
  }, [open, isMember, loaded, scrollToBottom]);

  useEffect(() => { if (open) scrollToBottom(); }, [messages, open, scrollToBottom]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setSending(true);
    const uid = `u${Date.now()}`, aid = `a${Date.now()}`;
    setMessages((m) => [...m, { id: uid, role: "user", text }, { id: aid, role: "assistant", text: "", pending: true }]);
    try {
      const token = localStorage.getItem("hstc_token");
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        credentials: "include",
        body: JSON.stringify({ message: text }),
      });
      if (!res.ok || !res.body) {
        let detail = "The Community Manager is unavailable right now. Please try again shortly.";
        try { const j = await res.json(); if (j.detail) detail = j.detail; } catch { /* ignore */ }
        setMessages((m) => { const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], text: detail, pending: false }; return c; });
        return;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        setMessages((m) => { const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], text: acc, pending: false }; return c; });
      }
    } catch {
      setMessages((m) => {
        const c = [...m];
        c[c.length - 1] = { ...c[c.length - 1], text: "Something went wrong reaching the Community Manager. Please try again.", pending: false };
        return c;
      });
    } finally {
      setSending(false);
    }
  };

  const clearThread = async () => {
    await api.delete("/chat").catch(() => {});
    setMessages([]);
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70]" data-testid="cm-drawer">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={chat.closeChat} />
      <div className="absolute right-0 top-0 h-full w-full sm:w-[420px] bg-charcoal border-l border-white/10 flex flex-col shadow-2xl animate-in slide-in-from-right duration-200">
        {/* Header */}
        <div className="flex items-center justify-between px-4 h-16 border-b border-white/10 bg-surface/60">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-gold/15 border border-gold/30 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-gold" />
            </div>
            <div>
              <div className="text-sm font-semibold text-white">Community Manager</div>
              <div className="text-[11px] text-emerald-400 flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 inline-block" /> Online · here to help 24/7
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {messages.length > 0 && (
              <button onClick={clearThread} title="Clear chat" data-testid="cm-clear-btn"
                className="p-2 text-white/50 hover:text-red-400 transition-colors">
                <Trash2 className="w-4 h-4" />
              </button>
            )}
            <button onClick={chat.closeChat} data-testid="cm-close-btn"
              className="p-2 text-white/60 hover:text-gold transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        {!user ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-4 px-8 text-center">
            <MessageCircle className="w-10 h-10 text-gold/60" />
            <p className="text-white/80" data-testid="cm-signin-prompt">Sign in to chat with the Community Manager.</p>
            <Link to="/login" onClick={chat.closeChat} data-testid="cm-signin-link"
              className="px-4 py-2 text-sm font-semibold bg-gold text-charcoal rounded-sm hover:bg-gold-hover transition-colors">
              Sign in
            </Link>
          </div>
        ) : !isMember ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-4 px-8 text-center">
            <MessageCircle className="w-10 h-10 text-gold/60" />
            <p className="text-white/80" data-testid="cm-upgrade-prompt">Become a Founding Member to chat with the Community Manager.</p>
            <Link to="/pricing" onClick={chat.closeChat}
              className="px-4 py-2 text-sm font-semibold bg-gold text-charcoal rounded-sm hover:bg-gold-hover transition-colors">
              Become a Member
            </Link>
          </div>
        ) : (
          <>
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="cm-message-list">
              {messages.length === 0 && (
                <div className="text-center text-white/50 text-sm px-6 py-10" data-testid="cm-empty">
                  <Sparkles className="w-8 h-8 text-gold/50 mx-auto mb-3" />
                  Hi{user.name ? `, ${user.name.split(" ")[0]}` : ""}! I'm your Community Manager. Ask me anything about cases, the Academy, the Defense League, or getting around HSTC.
                </div>
              )}
              {messages.map((m, i) => (
                <div key={m.id || i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    data-testid={`cm-msg-${m.role}-${i}`}
                    className={`max-w-[82%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                      m.role === "user"
                        ? "bg-gold text-charcoal rounded-br-sm"
                        : "bg-surface border border-white/10 text-white/90 rounded-bl-sm"
                    }`}
                  >
                    {m.role === "assistant" && m.pending && !m.text
                      ? <span className="inline-flex items-center gap-2 text-white/60">Community Manager is typing <TypingDots /></span>
                      : m.text}
                  </div>
                </div>
              ))}
            </div>

            {/* Composer */}
            <div className="border-t border-white/10 p-3 bg-surface/40">
              <div className="flex items-end gap-2">
                <textarea
                  data-testid="cm-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={onKeyDown}
                  rows={1}
                  placeholder="Message the Community Manager…"
                  className="flex-1 resize-none max-h-32 bg-charcoal border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder-white/30 focus:outline-none focus:border-gold/50"
                />
                <button
                  onClick={send}
                  disabled={sending || !input.trim()}
                  data-testid="cm-send-btn"
                  className="shrink-0 w-10 h-10 flex items-center justify-center rounded-xl bg-gold text-charcoal hover:bg-gold-hover disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
              <p className="text-[10px] text-white/30 mt-2 text-center">
                Educational guidance only. She won't name suspects or give legal advice.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
